/**
 * Codice fiscale – validatore “tutto-in-uno”
 * (c) 2025 – libero uso con attribuzione
 */

export interface FiscalCodeOptions {
    /** Cognome in chiaro (es. "Rossi") */
    surname?: string;
    /** Nome in chiaro (es. "Mario")   */
    name?: string;
    /** Data di nascita (Date o ISO "YYYY-MM-DD") */
    birthDate?: Date | string;
    /** Sesso ("M" | "F") */
    sex?: "M" | "F";
    /** Codice catastale di nascita, 4 caratteri */
    birthplaceCode?: string;
}

export interface ValidationResult {
    ok: boolean;                 // esito complessivo
    errors: string[];            // motivi dell’eventuale KO
    details: {                   // esiti dei singoli check
        format: boolean;
        checkDigit: boolean;
        surname?: boolean;
        name?: boolean;
        birthDate?: boolean;
        sex?: boolean;
        birthplace?: boolean;
    };
}

////////////////////////////////////////////////////////////////////////////////
// Costanti d’appoggio
////////////////////////////////////////////////////////////////////////////////

// Tabella omocodia – lettere → cifra
const OMO: Record<string, number> = {
    L: 0, M: 1, N: 2, P: 3, Q: 4, R: 5, S: 6, T: 7, U: 8, V: 9,
};

// Mesi
const MONTH: Record<string, number> = {
    A: 1, B: 2, C: 3, D: 4, E: 5, H: 6,
    L: 7, M: 8, P: 9, R: 10, S: 11, T: 12,
};


////////////////////////////////////////////////////////////////////////////////
// Funzioni di servizio
////////////////////////////////////////////////////////////////////////////////

/** Sostituisce eventuali lettere omocodiche con le corrispondenti cifre */
function deOmocode(code: string): string {
    return code
        .split("")
        .map((c, i) =>
            i >= 6 && i <= 14 && OMO[c] !== undefined ? String(OMO[c]) : c,
        )
        .join("");
}

/** Calcola la lettera di controllo attesa */
/**
 * Calcola il check digit di un codice fiscale italiano utilizzando i primi 15 caratteri.
 * @param cf - Il codice fiscale (16 caratteri) di cui calcolare il check digit
 * @returns Il carattere di controllo calcolato (l'ultima lettera)
 */
function computeCheckDigit(cf: string): string {
    // Valori per i caratteri in posizione pari (indice 0-based: 1,3,5,...)
    const valoriPari: Record<string, number> = {
        '0': 0, '1': 1, '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7, '8': 8, '9': 9,
        'A': 0, 'B': 1, 'C': 2, 'D': 3, 'E': 4, 'F': 5, 'G': 6, 'H': 7, 'I': 8, 'J': 9,
        'K': 10, 'L': 11, 'M': 12, 'N': 13, 'O': 14, 'P': 15, 'Q': 16, 'R': 17, 'S': 18, 'T': 19,
        'U': 20, 'V': 21, 'W': 22, 'X': 23, 'Y': 24, 'Z': 25
    };

    // Valori per i caratteri in posizione dispari (indice 0-based: 0,2,4,...)
    const valoriDispari: Record<string, number> = {
        '0': 1, '1': 0, '2': 5, '3': 7, '4': 9, '5': 13, '6': 15, '7': 17, '8': 19, '9': 21,
        'A': 1, 'B': 0, 'C': 5, 'D': 7, 'E': 9, 'F': 13, 'G': 15, 'H': 17, 'I': 19, 'J': 21,
        'K': 2, 'L': 4, 'M': 18, 'N': 20, 'O': 11, 'P': 3, 'Q': 6, 'R': 8, 'S': 12, 'T': 14,
        'U': 16, 'V': 10, 'W': 22, 'X': 25, 'Y': 24, 'Z': 23
    };

    // Lettere corrispondenti al resto della divisione per 26
    const lettereDiControllo = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

    // Normalizza il codice fiscale in maiuscolo
    const codiceFiscale = cf.toUpperCase();

    let somma = 0;

    // Considera solo i primi 15 caratteri
    for (let i = 0; i < 15; i++) {
        const carattere = codiceFiscale[i];
        // Posizioni 0-based: i pari sono dispari in 1-based e viceversa
        if (i % 2 === 0) { // posizione dispari (0, 2, 4...)
            somma += valoriDispari[carattere] || 0;
        } else { // posizione pari (1, 3, 5...)
            somma += valoriPari[carattere] || 0;
        }
    }

    // Calcola il resto della divisione per 26 e restituisce la lettera corrispondente
    return lettereDiControllo[somma % 26];
}

/** Estrae data di nascita e sesso dal codice (gestisce omocodia) */
export function extractBirth(cf: string): { date: Date; sex: "M" | "F" } | null {
    //const raw = deOmocode(cf);
    const raw = cf; 
    const year = parseInt(raw.slice(6, 8), 10);
    const monthLetter = raw[8];
    const month = MONTH[monthLetter];
    if (!month) return null;

    let day = parseInt(raw.slice(9, 11), 10);
    let sex: "M" | "F" = "M";
    if (day > 40) {
        sex = "F";
        day -= 40;
    }
    const fullYear = year >= 0 && year <= 24 ? 2000 + year : 1900 + year;
    const date = new Date(Date.UTC(fullYear, month - 1, day));
    return Number.isNaN(date.getTime()) ? null : { date, sex };
}

/** Converte cognome o nome in codice a 3 lettere (semplificato) */
function codeName(part: string, isName = false): string {
    const consonants = part
        .toUpperCase()
        .replace(/[^A-Z]/g, "")
        .replace(/[AEIOU]/g, "");
    const vowels = part.toUpperCase().replace(/[^AEIOU]/g, "");
    let res = (isName && consonants.length > 3
            ? consonants[0] + consonants[2] + consonants[3]
            : (consonants + vowels).padEnd(3, "X").slice(0, 3)
    );
    return res;
}

////////////////////////////////////////////////////////////////////////////////
// Funzione principale
////////////////////////////////////////////////////////////////////////////////

export function validateCodiceFiscale(
    codiceFiscale: string,
    options: FiscalCodeOptions = {},
): ValidationResult {
    const cf = codiceFiscale.toUpperCase().trim();
    const errors: string[] = [];
    const details: ValidationResult["details"] = { format: false, checkDigit: false };

    // 1. Verifica formale -------------------------------------------------------
    const formalOk = /^[A-Z0-9]{16}$/.test(cf);
    details.format = formalOk;
    if (!formalOk) errors.push("Formato non valido");

    // 2. Check-digit ------------------------------------------------------------
    const expectedCtrl = computeCheckDigit(cf);
    details.checkDigit = expectedCtrl === cf[15];
    if (!details.checkDigit) errors.push("Check-digit errato");

    // 3. Controlli opzionali ----------------------------------------------------
    if (options.surname) {
        const ok = codeName(options.surname) === cf.slice(0, 3);
        details.surname = ok;
        if (!ok) errors.push("Cognome non coerente");
    }

    if (options.name) {
        const ok = codeName(options.name, true) === cf.slice(3, 6);
        details.name = ok;
        if (!ok) errors.push("Nome non coerente");
    }

    if (options.birthDate || options.sex) {
        const ext = extractBirth(cf);
        if (ext) {
            if (options.birthDate) {
                const inDate =
                    typeof options.birthDate === "string"
                        ? new Date(options.birthDate)
                        : options.birthDate;
                const same =
                    inDate.getUTCFullYear() === ext.date.getUTCFullYear() &&
                    inDate.getUTCMonth() === ext.date.getUTCMonth() &&
                    inDate.getUTCDate() === ext.date.getUTCDate();
                details.birthDate = same;
                if (!same) errors.push("Data di nascita non coerente");
            }
            if (options.sex) {
                const same = options.sex.toUpperCase() === ext.sex;
                details.sex = same;
                if (!same) errors.push("Sesso non coerente");
            }
        } else {
            errors.push("Impossibile estrarre data/sesso");
        }
    }

    if (options.birthplaceCode) {
        const ok = options.birthplaceCode.toUpperCase() === cf.slice(11, 15);
        details.birthplace = ok;
        if (!ok) errors.push("Codice catastale non coerente");
    }

    // 4. Esito finale -----------------------------------------------------------
    const ok = errors.length === 0;
    return { ok, errors, details };
}
//TODO: i18n
